// Branded screens that stand in the call's place while it connects, waits for a
// tap, cannot find the call, could not start, or needs the mic. Ported from
// ottos-consult src/consult/CallScreens.jsx (PORTED-FROM.md), plus the mic help
// screen for each browser (design 7.8).
import type { ReactNode } from "react";
import { isAndroid, isIos } from "../device";
import type { MicProblem } from "./audio/micPermission";
import { micProblemText } from "../checkin/MicCheck";
import type { ConsultStrings } from "../strings";

function CallGateFrame({ s, children, live = false }: { s: ConsultStrings; children: ReactNode; live?: boolean }) {
  return (
    <main className="oo-call-gate" aria-live={live ? "polite" : undefined}>
      <div className="oo-call-gate__top">
        <img src="/images/consult/logo-badge-cream-on-blue.png" alt={s.site.brandAlt} />
        <span>
          {s.call.headerTitle} <i aria-hidden="true">&middot;</i> <em>{s.call.headerStep}</em>
        </span>
      </div>
      <div className="oo-call-gate__card">
        <div className="oo-call-gate__photo">
          <img src="/images/consult/otto-consult-call.jpg" alt={s.thanks.photoAlt} />
        </div>
        {children}
      </div>
    </main>
  );
}

export function CallConnecting({ s, title, detail }: { s: ConsultStrings; title: string; detail: string }) {
  return (
    <CallGateFrame s={s} live>
      <p className="oo-call-gate__status">
        <span aria-hidden="true" />
        {s.call.connecting}
      </p>
      <h1>{title}</h1>
      <p className="oo-call-gate__copy">{detail}</p>
    </CallGateFrame>
  );
}

export function CallRejoin({ s, onStart, starting }: { s: ConsultStrings; onStart: () => void; starting: boolean }) {
  return (
    <CallGateFrame s={s}>
      <p className="oo-call-gate__status">
        <span aria-hidden="true" />
        {s.call.keptPlace}
      </p>
      <h1>{s.call.rejoinTitle}</h1>
      <p className="oo-call-gate__copy">{s.call.rejoinDetail}</p>
      <button type="button" className="oo-call-gate__button" disabled={starting} onClick={onStart}>
        {starting ? s.call.rejoining : s.call.rejoinButton}
      </button>
    </CallGateFrame>
  );
}

export function CallMissing({ s }: { s: ConsultStrings }) {
  return (
    <CallGateFrame s={s}>
      <h1>{s.call.missingTitle}</h1>
      <p className="oo-call-gate__copy">{s.call.missingDetail}</p>
      <a className="oo-call-gate__button" href="/">
        {s.call.startNew}
      </a>
    </CallGateFrame>
  );
}

/** Protocol error codes to plain words (the server's message is safe too, but ours match the page's voice). */
export function failureText(s: ConsultStrings, code: string): string {
  switch (code) {
    case "already_connected":
      return s.call.alreadyOpen;
    case "token_expired":
    case "bad_token":
    case "wrong_consult":
    case "consult_not_found":
      return s.call.expired;
    case "consult_ended":
      return s.call.ended;
    case "voice_unavailable":
      return s.call.unavailable;
    case "at_capacity":
      return s.call.fullyBooked;
    default:
      return s.call.genericFailure;
  }
}

export function CallFailed({
  s,
  code,
  onRetry,
  retrying,
  canRetry,
}: {
  s: ConsultStrings;
  code: string;
  onRetry: () => void;
  retrying: boolean;
  canRetry: boolean;
}) {
  return (
    <CallGateFrame s={s}>
      <div role="alert">
        <p className="oo-call-gate__status is-error">
          <span aria-hidden="true" />
          {s.call.failedStatus}
        </p>
        <h1>{s.call.failedTitle}</h1>
        <p className="oo-call-gate__copy">{failureText(s, code)}</p>
      </div>
      {canRetry ? (
        <button type="button" className="oo-call-gate__button" disabled={retrying} onClick={onRetry}>
          {retrying ? s.call.trying : s.call.tryAgain}
        </button>
      ) : (
        <a className="oo-call-gate__button" href="/">
          {s.call.startNew}
        </a>
      )}
    </CallGateFrame>
  );
}

/** Mic denied or blocked: the steps for this browser, a retry, and the person route (design 7.8). */
export function MicHelp({
  s,
  problem,
  userAgent,
  onRetry,
  callbackUrl,
}: {
  s: ConsultStrings;
  problem: MicProblem;
  userAgent: string;
  onRetry: () => void;
  callbackUrl: string;
}) {
  const steps = isIos(userAgent) ? s.mic.iosSteps : isAndroid(userAgent) ? s.mic.androidSteps : s.mic.desktopSteps;
  const showSteps = problem === "denied";
  return (
    <CallGateFrame s={s}>
      <div role="alert">
        <p className="oo-call-gate__status is-error">
          <span aria-hidden="true" />
          {s.call.didNotStart}
        </p>
        <h1>{s.mic.helpTitle}</h1>
        <p className="oo-call-gate__copy">{micProblemText(s, problem)}</p>
        {showSteps && <p className="oo-call-gate__copy oo-call-gate__steps">{steps}</p>}
      </div>
      {problem !== "unavailable" && (
        <button type="button" className="oo-call-gate__button" onClick={onRetry}>
          {s.mic.tryAgain}
        </button>
      )}
      <p className="oo-call-gate__copy oo-call-gate__alt">
        <a href={callbackUrl}>{s.checkin.personLink}</a>
      </p>
    </CallGateFrame>
  );
}
